import React, { useCallback, useEffect, useMemo } from "react";
import { StyleSheet } from "react-native";
import { AuxiliarySalaryInput } from "../../../__generated__/graphql";
import Modal from "../../Modal";
import ScreenCard from "../../../screens/ScreenCard";
import SimpleHeader from "../../SimpleHeader";
import VStack from "../../../ui_atoms/VStack";
import { Controller, useForm } from "react-hook-form";
import TextField from "../../TextField";
import { useI18n } from "../../../hooks/i18n-hooks";
import { useTheme } from "../../../hooks/use-theme";
import Text from "../../Text";
import { useChainInputFocus } from "../../../hooks/use-chain-input-focus";
import { useValidateFieldMinMax } from "../../../hooks/use-validate-field-min-max";
import MoneyField from "../../MoneyField";
import QuantityField from "../../QuantityField";

export type CreateAuxiliarySalaryFormData = {
    description?: string;
    hours?: number;
    amount?: number;
};

type AuxiliarySalaryCreateModalProps = {
    onCreate: (auxiliarySalary: AuxiliarySalaryInput) => void;
    onClose: () => void;
    visible: boolean;
    initialData?: AuxiliarySalaryInput;
};

const AuxiliarySalaryCreateModal: React.FC<AuxiliarySalaryCreateModalProps> = ({
    onClose,
    onCreate,
    visible,
    initialData,
}) => {
    const styles = useStyles();
    const { fonts } = useTheme();
    const i18n = useI18n();

    const {
        control,
        handleSubmit,
        reset,
        formState: { isValid },
        setValue,
    } = useForm<CreateAuxiliarySalaryFormData>({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: {
            description: "",
            hours: undefined,
            amount: undefined,
        },
    });
    useEffect(() => {
        if (visible) {
            if (initialData) {
                setValue("description", initialData.description);
                setValue("hours", initialData.duration / 60);
                setValue("amount", initialData.grossAmount / 100);
            } else {
                reset();
            }
        }
    }, [initialData, reset, setValue, visible]);

    const handleCreateAuxiliarySalary = useCallback(
        (formData: CreateAuxiliarySalaryFormData) => {
            onCreate({
                description: formData.description!,
                duration: Math.round((formData.hours || 0) * 60),
                grossAmount: Math.round((formData.amount || 0) * 100),
            });
        },
        [onCreate],
    );

    const { chainInputFocus } = useChainInputFocus();
    const validateFieldMinMax = useValidateFieldMinMax();

    return (
        <Modal visible={visible} onClose={onClose} hideCloseButton>
            <SimpleHeader title={i18n.t("auxiliary_salary.title")} onClose={onClose} mode="small" />
            <ScreenCard
                buttons={[
                    {
                        onPress: handleSubmit(handleCreateAuxiliarySalary),
                        mode: "secondary",
                        isValid: isValid,
                        title: !initialData ? i18n.t("add") : i18n.t("edit"),
                    },
                ]}
                withAssistanceButton
            >
                <VStack gap={1} h="2/3">
                    <Controller
                        name="description"
                        control={control}
                        rules={{ required: true }}
                        render={({
                            field: { onChange, value, onBlur, name },
                            fieldState: { error },
                        }) => (
                            <TextField
                                label={i18n.t(`auxiliary_salary.field_description`)}
                                value={value}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                error={error?.message}
                                showErrorText
                                multiline
                                {...chainInputFocus(name)}
                            />
                        )}
                    />
                    <Controller
                        name="hours"
                        control={control}
                        rules={{
                            required: true,
                            validate: {
                                positive: (value) => (value || 0) > 0,
                                minMax: (value) =>
                                    validateFieldMinMax({
                                        value,
                                        max: 744,
                                    }),
                            },
                        }}
                        render={({
                            field: { onChange, value, onBlur, name },
                            fieldState: { error },
                        }) => (
                            <VStack gap="1/3">
                                <Text style={styles.sectionTitle}>
                                    {i18n.t("auxiliary_salary.hours_title")}
                                </Text>
                                <QuantityField
                                    value={value}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    decimalPlaces={2}
                                    error={error?.message}
                                    showErrorText
                                    {...chainInputFocus(name)}
                                />
                            </VStack>
                        )}
                    />
                    <Controller
                        name="amount"
                        control={control}
                        rules={{
                            required: true,
                            validate: {
                                positive: (value) => (value || 0) > 0,
                                minMax: (value) =>
                                    validateFieldMinMax({
                                        value,
                                        max: 1_000_000,
                                    }),
                            },
                        }}
                        render={({
                            field: { onChange, value, onBlur, name },
                            fieldState: { error },
                        }) => (
                            <VStack gap="1/3">
                                <>
                                    <Text style={styles.sectionTitle}>
                                        {i18n.t("auxiliary_salary.amount_title")}
                                    </Text>
                                    <Text style={styles.sectionSubtitle}>
                                        {i18n.t("auxiliary_salary.amount_subtitle")}
                                    </Text>
                                </>
                                <MoneyField
                                    value={value}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    error={error?.message}
                                    inputStyle={fonts.headlineMedium}
                                    showErrorText
                                    {...chainInputFocus(name)}
                                />
                            </VStack>
                        )}
                    />
                </VStack>
            </ScreenCard>
        </Modal>
    );
};

const useStyles = () => {
    const { fonts, colors } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                sectionTitle: {
                    ...fonts.bodyMedium,
                },
                sectionSubtitle: {
                    ...fonts.bodySmall,
                    color: colors.greyAccessible,
                },
            }),
        [colors.greyAccessible, fonts.bodyMedium, fonts.bodySmall],
    );
};

export default AuxiliarySalaryCreateModal;
